/**
 * Shell rendered for every authenticated workspace-scoped route:
 *
 *   /w/:slug              → redirects to /workspace
 *   /w/:slug/workspace    → workspace settings
 *   /w/:slug/members      → members
 *   /w/:slug/auth         → authentication (API keys + signing keys)
 *   /w/:slug/agents       → M7
 *   /w/:slug/kb           → M8
 *   /w/:slug/runs         → M9
 *   /w/:slug/handoff      → M9
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/nfr-design/frontend-architecture.md § 4.2`.
 *
 * Revamped to the "Final" (Attio) design language: a slim white sidebar beside a
 * flat, edge-to-edge white content surface. The per-screen header (title +
 * actions) is rendered by each page's `<PageShell>`; the shell itself no longer
 * carries a breadcrumb bar. The sidebar collapse trigger lives in the sidebar
 * header. Mirrors the Storybook `dashboard/final` reference.
 */

import { useAuth, useWorkspace } from "@cometchat-ai/auth/client";
import { isHelpdeskOnlyPrincipal } from "@cometchat-ai/auth/shared";
import { SidebarInset, SidebarProvider } from "@cometchat-ai/ui-dashboard";
import { motion, useReducedMotion } from "motion/react";
import type * as React from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation } from "react-router";

import { AppSidebar } from "../components/app-sidebar";
import { BreadcrumbTrailProvider } from "./breadcrumb-trail";
import { FINAL_FONT, FinalPolish } from "./final-polish";
import { SectionTitleContext } from "./section-title";

/**
 * Translation keys are typed via module augmentation in `src/i18n/types.ts`.
 * Map the first path segment after `/w/:slug` to its nav label so the per-page
 * header can fall back to the active section name.
 */
type NavLabelKey =
  | "nav.home"
  | "nav.agents"
  | "nav.contacts"
  | "nav.kb"
  | "nav.tables"
  | "nav.tools"
  | "nav.cards"
  | "nav.channels"
  | "nav.agentops"
  | "nav.conversations"
  | "nav.handoff"
  | "nav.inbox"
  | "nav.feedback"
  | "nav.workspace"
  | "nav.members"
  | "nav.roles"
  | "nav.authentication"
  | "nav.memory"
  | "nav.contextVariables"
  | "nav.featureFlags"
  | "nav.mcp"
  | "nav.workflows"
  | "nav.settings";

const SECTION_LABEL: Record<string, NavLabelKey> = {
  agents: "nav.agents",
  workflows: "nav.workflows",
  contacts: "nav.contacts",
  channels: "nav.channels",
  agentops: "nav.agentops",
  conversations: "nav.conversations",
  kb: "nav.kb",
  tables: "nav.tables",
  tools: "nav.tools",
  cards: "nav.cards",
  handoff: "nav.handoff",
  helpdesk: "nav.inbox",
  feedback: "nav.feedback",
  mcp: "nav.mcp",
  workspace: "nav.workspace",
  memory: "nav.memory",
  members: "nav.members",
  roles: "nav.roles",
  "context-variables": "nav.contextVariables",
  "feature-flags": "nav.featureFlags",
  auth: "nav.authentication",
  settings: "nav.settings",
};

export function WorkspaceShell() {
  const { workspace } = useWorkspace();
  const auth = useAuth();
  const deskOnly = isHelpdeskOnlyPrincipal(auth.permissions);
  const { t } = useTranslation("workspace");
  const location = useLocation();
  const prefersReduced = useReducedMotion();

  // The scroll container is this div, not the window, so react-router's
  // ScrollRestoration can't reset it — do it manually on pathname change.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [location.pathname]);

  const base = `/w/${workspace.slug}`;
  const rest = location.pathname.startsWith(base)
    ? location.pathname.slice(base.length).replace(/^\//, "")
    : "";
  const restParts = rest.split("/");
  const section = restParts[0] ?? "";
  const sectionKey = SECTION_LABEL[section];

  // Bare pages render full-height on the flat surface so they can host their own
  // multi-pane layout — e.g. the KB detail's Sources + Playground split, or the
  // Conversations three-pane master/detail.
  const bare =
    (section === "kb" && !!restParts[1] && restParts[1] !== "new") ||
    section === "conversations" ||
    // AgentOps run detail hosts its own full-height trace split with internal
    // scroll + pinned panel headers, so it needs the bounded layout too.
    (section === "agentops" && restParts[1] === "runs" && !!restParts[2]) ||
    // Table detail hosts the rows grid with a sticky header + pinned id column,
    // which only engage when the grid owns the scroll.
    (section === "tables" &&
      !!restParts[1] &&
      !["queries", "connectors", "logs"].includes(restParts[1]) &&
      !restParts[2]) ||
    section === "helpdesk" ||
    // Workspace settings hosts the storybook-style full-height Helpdesk
    // settings card (rail + scrolling content pane).
    section === "workspace";

  return (
    <div
      data-final-shell
      className="relative h-svh overflow-hidden bg-background [transform:translateZ(0)]"
      style={{ fontFamily: FINAL_FONT }}
    >
      <FinalPolish />
      <SidebarProvider
        // Desk-only operators live in the full-width Inbox — start with the
        // nav tucked to icons; the rail toggle brings it back.
        defaultOpen={!deskOnly}
        className="h-full min-h-0"
        style={
          {
            "--sidebar-width": "13.5rem",
            "--sidebar-width-icon": "calc(var(--space-8) + var(--space-4))",
          } as React.CSSProperties
        }
      >
        <AppSidebar />
        <SidebarInset className="h-full overflow-hidden bg-background">
          <SectionTitleContext.Provider value={sectionKey ? t(sectionKey) : section}>
            <BreadcrumbTrailProvider>
              <div
                ref={scrollRef}
                className="flex h-full min-h-0 flex-1 flex-col overflow-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {/* subtle per-page entrance — keyed on pathname so it replays on
                    navigation but not on in-page ?search/?tab changes. */}
                <motion.div
                  key={location.pathname}
                  initial={prefersReduced ? false : { opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className={
                    bare
                      ? "flex min-h-full flex-col lg:h-full lg:min-h-0"
                      : "flex min-h-full flex-col"
                  }
                >
                  <Outlet />
                </motion.div>
              </div>
            </BreadcrumbTrailProvider>
          </SectionTitleContext.Provider>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
